/**
 * Responsive design utilities and media queries
 */

import { breakpoints } from './tokens';

/**
 * Media query helpers for responsive design
 */
export const media = {
  xs: `@media (min-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
  xxl: `@media (min-width: ${breakpoints.xxl})`,
  
  // Max width queries
  xsMax: `@media (max-width: ${breakpoints.xs})`,
  smMax: `@media (max-width: ${breakpoints.sm})`,
  mdMax: `@media (max-width: ${breakpoints.md})`,
  lgMax: `@media (max-width: ${breakpoints.lg})`,
  xlMax: `@media (max-width: ${breakpoints.xl})`,
  
  // Mobile first
  mobile: `@media (max-width: ${breakpoints.sm})`,
  tablet: `@media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.lg})`,
  desktop: `@media (min-width: ${breakpoints.lg})`,
};

/**
 * Responsive container styles
 */
export const containerStyles = {
  maxWidth: {
    xs: '100%',
    sm: '540px',
    md: '720px',
    lg: '960px',
    xl: '1140px',
    xxl: '1320px',
  },
  padding: {
    mobile: '16px',
    tablet: '24px',
    desktop: '32px',
  },
};

/**
 * Common responsive patterns
 */
export const responsivePatterns = {
  // Flexbox patterns
  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row' as const,
  },
  
  // Grid patterns
  gridAuto: (columns: number) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${Math.floor(300 / columns)}px, 1fr))`,
    gap: '16px',
  }),
  
  // Text patterns
  textResponsive: {
    fontSize: 'clamp(14px, 2vw, 16px)',
    lineHeight: 1.5,
  },
  headingResponsive: (size: 'sm' | 'md' | 'lg') => {
    const sizes = {
      sm: 'clamp(18px, 3vw, 24px)',
      md: 'clamp(24px, 4vw, 32px)',
      lg: 'clamp(32px, 5vw, 48px)',
    };
    return {
      fontSize: sizes[size],
      lineHeight: 1.2,
    };
  },
  
  // Spacing patterns
  sectionPadding: {
    padding: '32px 16px',
    [media.md]: {
      padding: '48px 24px',
    },
    [media.lg]: {
      padding: '64px 32px',
    },
  },
};

/**
 * Mobile-specific utilities
 */
export const mobileUtils = {
  // Hide on mobile
  hideOnMobile: {
    [media.mobile]: {
      display: 'none',
    },
  },
  
  // Show only on mobile
  showOnMobile: {
    [media.tablet]: {
      display: 'none',
    },
    [media.desktop]: {
      display: 'none',
    },
  },
  
  // Touch-friendly hit areas
  touchTarget: {
    minHeight: '44px',
    minWidth: '44px',
    padding: '12px',
  },
  
  // Mobile-optimized form inputs
  mobileInput: {
    fontSize: '16px', // Prevents zoom on iOS
    padding: '12px',
    minHeight: '44px',
  },
};

/**
 * Accessibility and UX helpers
 */
export const a11yUtils = {
  // Screen reader only
  srOnly: {
    position: 'absolute' as const,
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap' as const,
    borderWidth: 0,
  },
  
  // Focus visible
  focusVisible: {
    '&:focus-visible': {
      outline: '2px solid #07988f',
      outlineOffset: '2px',
    },
  },
  
  // Reduced motion
  reducedMotion: {
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transition: 'none',
    },
  },
};
